import React, { useState, useEffect } from 'react';
import { FaPlug, FaCheckCircle, FaTimesCircle, FaFilter, FaSync, FaClock, FaExternalLinkAlt } from 'react-icons/fa';
import axios from '../../../lib/axios';
import Container from './styles';
import toast from 'react-hot-toast';

const WebhookEvents = () => {
  const [triggers, setTriggers] = useState([]);
  const [filteredTriggers, setFilteredTriggers] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'order.created', label: 'Order Created' },
    { value: 'order.updated', label: 'Order Updated' },
    { value: 'order.status_changed', label: 'Status Changed' },
    { value: 'shipment.picked_up', label: 'Shipment Picked Up' },
    { value: 'shipment.delivered', label: 'Delivered' },
    { value: 'shipment.failed', label: 'Failed' }
  ];

  useEffect(() => {
    fetchWebhookTriggers();
  }, []);

  useEffect(() => {
    console.log('Applying filters with triggers:', triggers.length);
    applyFilters();
  }, [triggers, filterEvent, filterStatus]);

  const fetchWebhookTriggers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/company/webhook-triggers?limit=100');
      console.log('Webhook triggers response:', response.data);
      if (response.data) {
        // Convert success from 0/1 to boolean for proper filtering
        const normalizedData = response.data.map(trigger => ({
          ...trigger,
          success: trigger.success === 1 || trigger.success === true
        }));
        console.log('Normalized triggers:', normalizedData);
        setTriggers(normalizedData);
      }
    } catch (error) {
      console.error('Error fetching webhook triggers:', error);
      toast.error('Failed to fetch webhook events');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...triggers];
    console.log('Before filtering:', filtered.length);

    if (filterEvent !== 'all') {
      filtered = filtered.filter(t => t.event === filterEvent);
      console.log(`After event filter (${filterEvent}):`, filtered.length);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => 
        filterStatus === 'success' ? t.success : !t.success
      );
      console.log(`After status filter (${filterStatus}):`, filtered.length);
    }

    console.log('Final filtered triggers:', filtered);
    setFilteredTriggers(filtered);
  };

  const viewTriggerDetails = (trigger) => {
    setSelectedTrigger(trigger);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (triggeredAt, completedAt) => {
    if (!completedAt) return 'N/A';
    const duration = completedAt - triggeredAt;
    return `${duration}ms`;
  };

  const getEventColor = (event) => {
    const colors = {
      'order.created': '#4CAF50',
      'order.updated': '#2196F3',
      'order.status_changed': '#FF9800',
      'shipment.picked_up': '#9C27B0',
      'shipment.delivered': '#4CAF50',
      'shipment.failed': '#F44336'
    };
    return colors[event] || '#757575';
  };

  const getStatusIcon = (success) => {
    return success ? <FaCheckCircle /> : <FaTimesCircle />;
  };

  return (
    <Container>
      <div className="webhook-events-header">
        <div className="header-content">
          <FaPlug className="header-icon" />
          <div>
            <h1>Webhook Events</h1>
            <p>Track and monitor all webhook deliveries and their responses</p>
          </div>
        </div>
        <button onClick={fetchWebhookTriggers} className="btn-refresh" disabled={loading}>
          <FaSync className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <FaFilter />
          <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
            {eventTypes.map(et => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="results-count">
          Showing {filteredTriggers.length} of {triggers.length} events
        </div>
      </div>

      <div className="webhook-events-container">
        {/* Sidebar - Event List */}
        <div className="events-sidebar">
          {loading ? (
            <div className="loading-state">
              <FaSync className="spinning" />
              <p>Loading events...</p>
            </div>
          ) : filteredTriggers.length === 0 ? (
            <div className="empty-state">
              <FaPlug />
              <p>No webhook events found</p>
            </div>
          ) : (
            <div className="events-list">
              {filteredTriggers.map((trigger) => (
                <div
                  key={trigger.id}
                  className={`event-card ${selectedTrigger?.id === trigger.id ? 'active' : ''} ${trigger.success ? 'success' : 'failed'}`}
                  onClick={() => viewTriggerDetails(trigger)}
                >
                  <div className="event-card-header">
                    <span className="status-icon" style={{ color: trigger.success ? '#4CAF50' : '#F44336' }}>
                      {getStatusIcon(trigger.success)}
                    </span>
                    <span 
                      className="event-badge" 
                      style={{ backgroundColor: getEventColor(trigger.event) }}
                    >
                      {trigger.event}
                    </span>
                  </div>
                  <div className="event-card-body">
                    <div className="event-url">{trigger.url}</div>
                    {trigger.httpStatus && (
                      <div className="event-status">HTTP {trigger.httpStatus}</div>
                    )}
                  </div>
                  <div className="event-card-footer">
                    <span className="event-time">
                      <FaClock /> {formatTimestamp(trigger.triggeredAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content - Event Details */}
        <div className="events-main">
          {!selectedTrigger ? (
            <div className="empty-state-main">
              <FaPlug className="empty-icon" />
              <h3>No event selected</h3>
              <p>Select an event from the list to view details</p>
            </div>
          ) : (
            <div className="event-details">
              <div className="event-details-header">
                <h2>Event Details</h2>
                <span className={`status-badge ${selectedTrigger.success ? 'success' : 'failed'}`}>
                  {getStatusIcon(selectedTrigger.success)}
                  {selectedTrigger.success ? 'Success' : 'Failed'}
                </span>
              </div>

              <div className="detail-section">
                <h3>Overview</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Event Type</label>
                    <span 
                      className="event-badge-large" 
                      style={{ backgroundColor: getEventColor(selectedTrigger.event) }}
                    >
                      {selectedTrigger.event}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Webhook URL</label>
                    <div className="url-display">
                      <code>{selectedTrigger.url}</code>
                      <a href={selectedTrigger.url} target="_blank" rel="noopener noreferrer">
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Triggered At</label>
                    <span>{formatTimestamp(selectedTrigger.triggeredAt)}</span>
                  </div>
                  {selectedTrigger.completedAt && (
                    <div className="detail-item">
                      <label>Duration</label>
                      <span>{formatDuration(selectedTrigger.triggeredAt, selectedTrigger.completedAt)}</span>
                    </div>
                  )}
                  {selectedTrigger.orderId && (
                    <div className="detail-item">
                      <label>Order ID</label>
                      <code>{selectedTrigger.orderId}</code>
                    </div>
                  )}
                  {selectedTrigger.httpStatus && (
                    <div className="detail-item">
                      <label>HTTP Status</label>
                      <span className={`http-status ${selectedTrigger.success ? 'success' : 'failed'}`}>
                        {selectedTrigger.httpStatus}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTrigger.payload && (
                <div className="detail-section">
                  <h3>Request Payload</h3>
                  <pre className="json-viewer">
                    {JSON.stringify(JSON.parse(selectedTrigger.payload), null, 2)}
                  </pre>
                </div>
              )}

              {selectedTrigger.responseBody && (
                <div className="detail-section">
                  <h3>Response Body</h3>
                  <pre className="response-viewer">
                    {selectedTrigger.responseBody}
                  </pre>
                </div>
              )}

              {selectedTrigger.responseHeaders && (
                <div className="detail-section">
                  <h3>Response Headers</h3>
                  <pre className="json-viewer">
                    {JSON.stringify(JSON.parse(selectedTrigger.responseHeaders), null, 2)}
                  </pre>
                </div>
              )}

              {selectedTrigger.errorMessage && (
                <div className="detail-section error-section">
                  <h3>Error Message</h3>
                  <div className="error-message">
                    {selectedTrigger.errorMessage}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default WebhookEvents;
